import { useEffect, useRef } from 'react';
import { approved } from '../content/copy';
import type { Material, MeshStandardMaterial } from 'three';

const GLASSES_URL = '/models/meta-glasses.glb';
// Head placement measured from the reference hero, as fractions of the canvas.
// Spline keeps a fixed vertical field of view, so head size follows canvas height.
const FRAME = {
  // Width is capped so the whole figure fits inside the canvas like the reference (0.518 of height).
  desktop: { centerX: 0.4757, headTop: 0.1445, headWidth: 0.171, maxWidth: 0.518 },
  mobile: { centerX: 0.4854, headTop: 0.14, headWidth: 0.2, maxWidth: 0.518 },
};
// Motion values copied from the reference's Webflow interactions (radians).
// One full turn over the (now shorter) hero, so he faces you again for the punchline.
const SCROLL_TURN = Math.PI * 2;
const MOUSE_YAW = 1; // "ryan -> mouse-movement": inner rotationY -1 -> 1 across the viewport
// Inner rotationZ 0.2 -> -0.2 down the viewport; the reference camera looks along X, so it reads as a nod.
const MOUSE_NOD = 0.2;
const SCROLL_SMOOTHING = 0.5; // Webflow smoothing 50
const MOUSE_SMOOTHING = 0.9; // Webflow smoothing 90

export function ModelStage() {
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = host.current;
    if (!element) return;
    let disposed = false;
    let cleanup = () => {};

    (async () => {
      const THREE = await import('three');
      const { buildNoob, GLASSES } = await import('./noob');
      const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
      const { MeshoptDecoder } = await import('three/examples/jsm/libs/meshopt_decoder.module.js');
      const { RoomEnvironment } =
        await import('three/examples/jsm/environments/RoomEnvironment.js');
      if (disposed) return;

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.NeutralToneMapping;
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      element.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      const pmrem = new THREE.PMREMGenerator(renderer);
      scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      scene.environmentIntensity = 0.3;
      const key = new THREE.DirectionalLight(0xffffff, 1.05);
      key.position.set(2, 3, 6);
      // Soft contact shadows (head on shoulders, chain on chest) give the clean blocks depth.
      key.castShadow = true;
      key.shadow.mapSize.set(2048, 2048);
      key.shadow.camera.left = key.shadow.camera.bottom = -1.2;
      key.shadow.camera.right = key.shadow.camera.top = 1.2;
      key.shadow.camera.near = 0.1;
      key.shadow.camera.far = 20;
      key.shadow.radius = 6;
      key.shadow.bias = -0.0004;
      key.shadow.normalBias = 0.01;
      scene.add(key, new THREE.HemisphereLight(0xffffff, 0xb9ad94, 0.25));

      // Same hierarchy as the reference scene: Outer spins with scroll, Inner follows the mouse.
      const outer = new THREE.Group();
      const inner = new THREE.Group();
      outer.add(inner);
      scene.add(outer);
      // He stands ~5-6 units away; a near plane of 0.01 wasted almost all depth precision.
      const camera = new THREE.PerspectiveCamera(22, 1, 1, 100);
      const desktop = matchMedia('(min-width: 992px)');
      const head = { top: 0, width: 1 };
      let bodyWidth = 1;

      const resize = () => {
        const { width, height } = element.getBoundingClientRect();
        if (!width || !height) return;
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        const frame = FRAME[desktop.matches ? 'desktop' : 'mobile'];
        const unitsPerPixel = Math.max(
          head.width / (frame.headWidth * height),
          bodyWidth / (frame.maxWidth * height),
        );
        const viewHeight = height * unitsPerPixel;
        camera.position.z = viewHeight / 2 / Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
        camera.updateProjectionMatrix();
        outer.position.set(
          (frame.centerX - 0.5) * width * unitsPerPixel,
          (0.5 - frame.headTop) * viewHeight - head.top,
          0,
        );
      };

      {
        const model = buildNoob();
        // Metals live on reflections: give them the full environment, not the dimmed scene level.
        model.traverse((node) => {
          if (!(node instanceof THREE.Mesh)) return;
          const material = node.material as MeshStandardMaterial;
          if (material.metalness > 0.5 || material.name === 'reflective') {
            material.envMap = scene.environment;
            material.envMapIntensity = 1;
          }
        });
        // Spin around the body's vertical centre line.
        const bounds = new THREE.Box3().setFromObject(model);
        const centre = bounds.getCenter(new THREE.Vector3());
        model.position.set(-centre.x, -centre.y, -centre.z);
        model.updateMatrixWorld(true);
        // Measure the head itself (not the glasses) from its top slice.
        const slice = bounds.max.y - (bounds.max.y - bounds.min.y) * 0.12;
        let left = Infinity;
        let right = -Infinity;
        const point = new THREE.Vector3();
        model.traverse((node) => {
          if (!(node instanceof THREE.Mesh) || node.name !== 'head') return;
          const positions = node.geometry.attributes.position;
          for (let index = 0; index < positions.count; index++) {
            point.fromBufferAttribute(positions, index).applyMatrix4(node.matrixWorld);
            if (point.y + centre.y < slice) continue;
            left = Math.min(left, point.x);
            right = Math.max(right, point.x);
          }
        });
        head.width = right - left;
        head.top = bounds.max.y - centre.y;
        bodyWidth = bounds.max.x - bounds.min.x;
        inner.add(model);
        resize();

        // Real Ray-Ban Meta shape (Rodin mesh, split into parts offline) with clean finishes.
        const finishes: Record<string, () => Material> = {
          acetate: () =>
            new THREE.MeshPhysicalMaterial({
              color: '#0b0b0d',
              roughness: 0.22,
              clearcoat: 0.8,
              clearcoatRoughness: 0.08,
              envMap: scene.environment,
              envMapIntensity: 0.55,
            }),
          lens: () =>
            new THREE.MeshPhysicalMaterial({
              color: '#26352c',
              roughness: 0.03,
              clearcoat: 1,
              clearcoatRoughness: 0.01,
              envMap: scene.environment,
              envMapIntensity: 1.6,
              side: THREE.DoubleSide,
            }),
          chrome: () =>
            new THREE.MeshPhysicalMaterial({
              color: '#e8ebee',
              metalness: 1,
              roughness: 0.14,
              envMap: scene.environment,
              envMapIntensity: 1,
            }),
        };
        new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).load(GLASSES_URL, (gltf) => {
          if (disposed) return;
          const glasses = gltf.scene;
          const box = new THREE.Box3().setFromObject(glasses);
          // The model's front faces +z; fit the frame front to the face.
          const scale = GLASSES.width / (box.max.x - box.min.x);
          const frameBack = box.max.z - (box.max.z - box.min.z) * 0.1;
          glasses.scale.setScalar(scale);
          glasses.position.set(
            -((box.max.x + box.min.x) / 2) * scale,
            GLASSES.y - ((box.max.y + box.min.y) / 2) * scale,
            GLASSES.front - frameBack * scale,
          );
          glasses.traverse((node) => {
            if (!(node instanceof THREE.Mesh)) return;
            node.castShadow = true;
            const finish = finishes[(node.material as Material).name] ?? finishes.acetate;
            (node.material as Material).dispose();
            node.material = finish();
          });
          model.add(glasses);
        });
      }

      const observer = new ResizeObserver(resize);
      observer.observe(element);

      const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const hero = element.closest('.intro-grid') ?? element;
      const target = { yaw: 0, nod: 0, turn: 0 };
      const onPointer = (event: PointerEvent) => {
        if (!desktop.matches) return;
        target.yaw = ((event.clientX / innerWidth) * 2 - 1) * MOUSE_YAW;
        target.nod = ((event.clientY / innerHeight) * 2 - 1) * MOUSE_NOD;
      };
      const onLeave = () => {
        target.yaw = 0;
        target.nod = 0;
      };
      // The turn completes as the punchline reaches mid-screen, so he faces you when he speaks.
      const punchline = hero.querySelector('.reel-heading');
      // Counted from the top of the page, not the hero, so the first pixel of scroll turns him
      // (phones pad the hero down, which used to leave a dead zone before he moved).
      const onScroll = () => {
        const rect = hero.getBoundingClientRect();
        const end = punchline
          ? punchline.getBoundingClientRect().top + scrollY - innerHeight * 0.55
          : rect.bottom + scrollY;
        // Never past the furthest the page can scroll, so he always completes the turn.
        const limit = Math.min(end, document.documentElement.scrollHeight - innerHeight);
        const progress = Math.min(Math.max(scrollY / Math.max(limit, 1), 0), 1);
        target.turn = progress * SCROLL_TURN;
      };
      if (!still) {
        addEventListener('pointermove', onPointer, { passive: true });
        document.documentElement.addEventListener('mouseleave', onLeave);
        addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }

      let onScreen = true;
      const seen = new IntersectionObserver(([entry]) => {
        onScreen = entry.isIntersecting;
      });
      seen.observe(element);

      // Webflow smooths once per frame; scale that to the real frame time.
      const ease = (smoothing: number, delta: number) => 1 - Math.pow(smoothing, delta * 60);
      const timer = new THREE.Timer();
      const stage = element.parentElement;
      const speech = stage?.querySelector<HTMLElement>('.speech');
      const headPoint = new THREE.Vector3();
      const headSide = new THREE.Vector3();
      const sideOffset = new THREE.Vector3();
      renderer.setAnimationLoop(() => {
        timer.update();
        const delta = Math.min(timer.getDelta(), 0.1);
        if (!onScreen) return;
        const mouse = ease(MOUSE_SMOOTHING, delta);
        inner.rotation.y += (target.yaw - inner.rotation.y) * mouse;
        inner.rotation.x += (target.nod - inner.rotation.x) * mouse;
        outer.rotation.y += (target.turn - outer.rotation.y) * ease(SCROLL_SMOOTHING, delta);
        renderer.render(scene, camera);
        // Pin the speech bubble to the left of his head at eye level, kept inside the viewport.
        if (stage && speech) {
          headPoint.set(0, head.top - head.width * 0.45, 0);
          inner.localToWorld(headPoint);
          headSide.copy(headPoint).add(sideOffset.set(head.width / 2, 0, 0));
          headPoint.project(camera);
          headSide.project(camera);
          const canvas = element.getBoundingClientRect();
          const box = stage.getBoundingClientRect();
          const toX = (ndc: number) => canvas.left + ((ndc + 1) / 2) * canvas.width;
          const left = toX(headPoint.x) - Math.abs(toX(headSide.x) - toX(headPoint.x));
          const gap = 72;
          const x = Math.max(left - gap - speech.offsetWidth, 16);
          stage.style.setProperty('--speech-x', `${x - box.left}px`);
          stage.style.setProperty(
            '--speech-y',
            `${canvas.top - box.top + ((1 - headPoint.y) / 2) * canvas.height}px`,
          );
        }
      });

      cleanup = () => {
        renderer.setAnimationLoop(null);
        removeEventListener('pointermove', onPointer);
        removeEventListener('scroll', onScroll);
        document.documentElement.removeEventListener('mouseleave', onLeave);
        observer.disconnect();
        seen.disconnect();
        scene.traverse((node) => {
          if (!(node instanceof THREE.Mesh)) return;
          node.geometry.dispose();
          const material = node.material as MeshStandardMaterial;
          for (const map of [material.map, material.normalMap, material.roughnessMap])
            map?.dispose();
          material.dispose();
        });
        scene.environment?.dispose();
        pmrem.dispose();
        renderer.dispose();
        renderer.domElement.remove();
      };
    })();

    return () => {
      disposed = true;
      cleanup();
    };
  }, []);

  return (
    <div className="model-stage">
      <div className="model-visual" ref={host} aria-hidden="true" />
      <p className="assistance speech">
        <img className="speech-avatar" src="/images/loader-face.webp" alt="" />
        <span className="speech-typing" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <span className="speech-text">
          {approved.assistance}{' '}
          <span className="assistance-icons">
            <img src="/icons/codex.svg" alt="Codex" />
            <img src="/icons/claude.svg" alt="Claude" />
            <img src="/icons/higgsfield.svg" alt="Higgsfield" />
          </span>
        </span>
      </p>
    </div>
  );
}
